import { useToast } from '@entur/alert'
import { Employee, Fetch, ManualOrder, StationPair, StationPairRequest } from '../api-clients/api-types'
import { useApi } from '../api-clients/client-hooks'
import {
  getEmployee,
  patchManualOrder,
  postManualOrder,
  fetchDeleteManualOrder,
  postStationPairs,
} from '../api-clients/eba-clients'
import { useEmployee } from '../contexts/employee-context'
import { trackEvent } from '../utils/tracking-utils'
import { capitalize } from '../utils/string-utils'

export function useCreateManualOrder(
  successCallback?: (manualOrder: ManualOrder) => void
): [
  Fetch<ManualOrder>,
  (ticketTypeId: number, fromDate: string, toDate: string, stations?: StationPairRequest[]) => void,
] {
  const { employee } = useEmployee()
  const [manualOrderResponse, addManualOrder] = useApi(postManualOrder, {
    successCallback: (data) => {
      if (successCallback) {
        successCallback(data)
      }
    },
  })

  const setManualOrder = (
    ticketTypeId: number,
    fromDate: string,
    toDate: string,
    stations?: StationPairRequest[]
  ) => {
    trackEvent('Register-Manual-Order', { category: 'Employee' })
    addManualOrder({
      id: employee.id,
      body: {
        ticketTypeId,
        fromDate,
        toDate,
        stations,
      },
    })
  }

  return [manualOrderResponse, setManualOrder]
}

export function useUpdateManualOrder(successCallback?: () => void): {
  updateManualOrderResponse: Fetch<ManualOrder>
  deleteManualOrderResponse: Fetch<ManualOrder>
  addStationPairResponse: Fetch<StationPair>
  fetchUpdatedEmployeeResponse: Fetch<Employee>
  updateManualOrder: (updateData: {
    id: number
    ticketTypeId?: number
    fromDate?: string
    toDate?: string
  }) => void
  deleteManualOrder: (id: number) => void
  addStationPair: (id: number, fromStation: string, toStation: string) => void
} {
  const { employee, setEmployee } = useEmployee()
  const { addToast } = useToast()
  const [fetchUpdatedEmployeeResponse, doFetchEmployee] = useApi(getEmployee, {
    successCallback: (data) => setEmployee(data),
  })

  const manualOrderSuccessCallback = () => {
    doFetchEmployee({
      id: employee.id,
    })
    if (successCallback) {
      successCallback()
    }
  }
  const [addStationPairResponse, doPostStationPairs] = useApi(postStationPairs, {
    successCallback: () => {
      manualOrderSuccessCallback()
      addToast({
        title: 'Rettighet endret!',
        content: `Stasjoner er lagt til.`,
        variant: 'success',
      })
    },
  })
  const [updateManualOrderResponse, doPatchManualOrder] = useApi(patchManualOrder, {
    successCallback: (data) => {
      manualOrderSuccessCallback()
      addToast({
        title: 'Rettighet endret!',
        content: `${capitalize(data.ticketType.description)} endret.`,
        variant: 'success',
      })
    },
  })
  const [deleteManualOrderResponse, doDeleteManualOrder] = useApi(fetchDeleteManualOrder, {
    successCallback: () => {
      manualOrderSuccessCallback()
      addToast({
        title: 'Rettigheten er slettet!',
        content: 'Synkroniser rettigheten for å fullføre slettingen.',
        variant: 'success',
      })
    },
    errorCallback: (error) => {
      if (error === 'Not Found') {
        addToast({
          title: 'Rettigheten er slettet!',
          content: 'Synkroniser rettigheten for å fullføre slettingen.',
          variant: 'success',
        })
      }
    },
  })

  const updateManualOrder = (updateData: {
    id: number
    ticketTypeId?: number
    fromDate?: string
    toDate?: string
  }) => {
    trackEvent('Update-Manual-Order', { category: 'Employee' })
    doPatchManualOrder({
      id: `${updateData.id}-${employee.id}`,
      body: {
        ticketTypeId: updateData.ticketTypeId,
        fromDate: updateData.fromDate,
        toDate: updateData.toDate,
      },
    })
  }

  const deleteManualOrder = (id: number) => {
    trackEvent('Delete-Manual-Order', { category: 'Employee' })
    doDeleteManualOrder({
      id: `${id}-${employee.id}`,
    })
  }

  const addStationPair = (id: number, fromStation: string, toStation: string) => {
    trackEvent('Add-Station-Pair', { category: 'Employee' })
    doPostStationPairs({
      id: `${id}-${employee.id}`,
      body: {
        fromStation,
        toStation,
      },
    })
  }

  return {
    updateManualOrderResponse,
    deleteManualOrderResponse,
    addStationPairResponse,
    fetchUpdatedEmployeeResponse,
    updateManualOrder,
    deleteManualOrder,
    addStationPair,
  }
}
